<template>
  <div class="admin-action-page">
    <CCard class="border-0 shadow-sm action-shell">
      <CCardBody class="p-4 p-lg-5">
        <div class="page-head">
          <div>
            <div class="eyebrow">{{ workspaceEyebrow }}</div>
            <h1>{{ workspaceTitle }}</h1>
            <p>{{ workspaceSubtitle }}</p>
          </div>
          <div class="page-head__meta">
            <span class="meta-chip">{{ agreement ? agreement.documentCode : text.loadingDots }}</span>
            <span class="meta-chip meta-chip--status">{{ agreement ? statusLabel : text.loading }}</span>
          </div>
        </div>

        <div v-if="agreement" class="action-layout">
          <div class="action-main">
            <div class="surface-card">
              <div class="surface-title">{{ activePreviewTitle }}</div>
              <div class="pdf-sheet">
                <div class="pdf-topline">
                  <span>{{ previewAttachmentLabel }}</span>
                  <span>{{ agreement.documentCode }}</span>
                </div>
                <div v-if="previewLoading" class="empty-state">{{ text.loadingDraft }}</div>
                <template v-else-if="documentSections.length">
                  <AgreementDocumentHeader
                    class="pdf-sheet__branding"
                    :title="agreement.title"
                    :partner="agreement.partner"
                    :partner-logo="agreement.partnerLogo"
                    :agreement-type="agreement.type"
                    :country="agreement.country"
                    title-label="Agreement Title"
                    compact
                    :show-meta="true"
                  />
                  <div class="draft-preview-stack">
                    <div
                      v-for="section in documentSections"
                      :key="section.key"
                      class="draft-preview-card"
                      :class="{ 'draft-preview-card--centered': isCenteredDocumentSection(section) }"
                    >
                      <div v-if="sectionDisplayTitle(section)" class="draft-preview-card__title">{{ sectionDisplayTitle(section) }}</div>
                      <AgreementSignaturePreview
                        v-if="isSignatureDocumentSection(section)"
                        :section="section"
                        left-header="Mae Fah Luang University"
                        :right-header="[agreement && agreement.partner, agreement && agreement.country].filter(Boolean).join('\n')"
                      />
                      <div v-else class="draft-preview-card__copy">{{ section.content }}</div>
                    </div>
                  </div>
                </template>
                <div v-else-if="previewUrl" class="empty-state empty-state--doc">
                  <strong>{{ text.wordAttached }}</strong>
                  <p>{{ previewAttachmentLabel }}</p>
                  <a :href="previewUrl" target="_blank" rel="noopener">{{ text.openDocument }}</a>
                </div>
                <div v-else class="empty-state">
                  <strong>{{ text.noWordDocument }}</strong>
                  <p>{{ previewError || text.noWordDocumentDetail }}</p>
                </div>
              </div>
            </div>

          </div>

          <div class="action-side">
            <div class="surface-card">
              <div class="surface-title">{{ text.detailsTitle }}</div>
              <div class="detail-grid compact">
                <div class="detail-row"><span>{{ text.fields.title }}</span><strong>{{ agreement.title }}</strong></div>
                <div class="detail-row"><span>{{ text.fields.createdBy }}</span><strong>{{ agreement.createdBy || '-' }}</strong></div>
                <div class="detail-row"><span>{{ text.fields.partner }}</span><strong>{{ agreement.partner || '-' }}</strong></div>
                <div class="detail-row"><span>{{ text.fields.type }}</span><strong>{{ agreement.type || '-' }}</strong></div>
                <div class="detail-row"><span>{{ text.fields.department }}</span><strong>{{ agreement.department || '-' }}</strong></div>
                <div class="detail-row"><span>{{ text.fields.submittedOn }}</span><strong>{{ formatDate(agreement.submittedAt) }}</strong></div>
                <div class="detail-row"><span>{{ text.fields.committeeMembers }}</span><strong>{{ trackedCommitteeCount }}</strong></div>
                <div class="detail-row"><span>{{ text.fields.totalComments }}</span><strong>{{ (agreement.comments || []).length }}</strong></div>
                <div class="detail-row detail-row--wide"><span>{{ text.fields.summary }}</span><strong>{{ agreement.summary || text.noSummary }}</strong></div>
              </div>
            </div>

            <div class="surface-card committee-progress-card">
              <div class="surface-title">{{ text.committeeProgressTitle }}</div>
              <div class="committee-progress-metrics">
                <div class="committee-progress-metric committee-progress-metric--approved">
                  <span>{{ text.committeeApprovedCount }}</span>
                  <strong>{{ committeeApprovedCount }}/{{ committeeRequiredCount }}</strong>
                </div>
                <div class="committee-progress-metric committee-progress-metric--pending">
                  <span>{{ text.committeePendingCount }}</span>
                  <strong>{{ committeePendingCount }}</strong>
                </div>
              </div>
              <div class="committee-progress-group">
                <div class="committee-progress-label">{{ text.committeeApprovedMembers }}</div>
                <div v-if="approvedCommitteeMembers.length" class="committee-pill-list">
                  <span
                    v-for="member in approvedCommitteeMembers"
                    :key="`approved-${member.reviewerId || member.reviewerName}`"
                    class="committee-pill committee-pill--approved"
                  >
                    {{ member.reviewerName }}
                  </span>
                </div>
                <div v-else class="committee-progress-empty">{{ text.committeeApprovedEmpty }}</div>
              </div>
              <div class="committee-progress-group">
                <div class="committee-progress-label">{{ text.committeePendingMembers }}</div>
                <div v-if="pendingCommitteeMembers.length" class="committee-pill-list">
                  <span
                    v-for="member in pendingCommitteeMembers"
                    :key="`pending-${member.reviewerId || member.reviewerName}`"
                    class="committee-pill committee-pill--pending"
                  >
                    {{ member.reviewerName }}
                  </span>
                </div>
                <div v-else class="committee-progress-empty">{{ pendingCommitteeEmptyMessage }}</div>
              </div>
            </div>

            <div class="surface-card surface-card--comments">
              <div class="surface-title">{{ text.commentsTitle }}</div>
              <div v-if="displayComments.length" class="comment-list">
                <div v-for="comment in displayComments" :key="comment.id || comment.commentId" class="comment-card">
                  <div class="comment-head">
                    <strong>{{ comment.author }}</strong>
                    <span>{{ formatDate(comment.date || comment.time) }}</span>
                  </div>
                  <div class="comment-meta">{{ comment.roleLabel || comment.role }} · {{ comment.articleTitle || comment.section || text.general }} · {{ text.page }} {{ comment.page || '-' }}</div>
                  <div class="comment-copy">{{ comment.copy || comment.content }}</div>
                </div>
              </div>
              <div v-else class="empty-state">{{ text.noComments }}</div>
            </div>

            <div class="surface-card">
              <div class="surface-title">{{ actionPanelTitle }}</div>
              <div v-if="showDecisionControls">
                <div v-if="!readyForAdminFinalApproval" class="decision-gate-note">
                  {{ committeeApprovalPendingMessage }}
                </div>
                <div class="decision-choice-grid">
                  <button
                    type="button"
                    class="decision-choice decision-choice--approve"
                    :class="{ 'decision-choice--selected': selectedDecision === 'approve' }"
                    @click="chooseDecision('approve')"
                  >
                    <strong>{{ text.approve }}</strong>
                    <span>{{ text.approveHelp }}</span>
                  </button>
                  <button
                    type="button"
                    class="decision-choice decision-choice--reject"
                    :class="{ 'decision-choice--selected': selectedDecision === 'reject' }"
                    @click="chooseDecision('reject')"
                  >
                    <strong>{{ text.disapprove }}</strong>
                    <span>{{ text.disapproveHelp }}</span>
                  </button>
                </div>
                <label v-if="selectedDecision === 'reject'" class="decision-label" for="decision-note">
                  {{ text.adminNote }}
                </label>
                <textarea
                  v-if="selectedDecision === 'reject'"
                  id="decision-note"
                  v-model="decisionNote"
                  class="decision-note"
                  rows="6"
                  :placeholder="text.adminNotePlaceholder"
                />
                <div class="decision-submit">
                  <CButton color="primary" :disabled="!selectedDecision || !readyForAdminFinalApproval" @click="submitDecision">{{ text.submitFinalDecision }}</CButton>
                </div>
              </div>
              <div v-else class="review-mode-panel">
                <div class="review-mode-panel__title">
                  {{ readyForAdminFinalApproval ? text.reviewCompletedTitle : text.reviewWaitingTitle }}
                </div>
                <p class="review-mode-panel__copy">
                  {{ readyForAdminFinalApproval ? text.reviewCompletedHelp : committeeApprovalPendingMessage }}
                </p>
                <div v-if="readyForAdminFinalApproval" class="decision-submit">
                  <CButton color="primary" @click="goToFinalApproval">{{ text.openFinalApproval }}</CButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="empty-state">{{ text.loadingWorkspace }}</div>

        <div class="footer-actions">
          <CButton color="secondary" variant="outline" @click="$router.push('/review-queue')">{{ text.backToQueue }}</CButton>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import api from '@/service/api'
import {
  getAgreementSectionDisplayTitle,
  isCenteredAgreementSection,
  isSignatureAgreementSection,
  normalizeAgreementSections
} from '@/utils/agreement-preview-sections'
import { createWorkflowNotification } from '@/utils/workflow-notifications'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'
import { showWarningAlert } from '@/utils/sweet-alert'
import AgreementDocumentHeader from '@/views/shared/AgreementDocumentHeader'
import AgreementSignaturePreview from '@/views/shared/AgreementSignaturePreview'

function normalizeComparable(value) {
  return String(value || '').trim().toLowerCase()
}

function normalizeCommitteeMember(item, fallbackLabel = 'Committee member') {
  return {
    reviewerId: String(item && (item.reviewerId || item.id) ? item.reviewerId || item.id : '').trim(),
    reviewerName: String(item && (item.reviewerName || item.name) ? item.reviewerName || item.name : '').trim() || fallbackLabel,
    decision: normalizeComparable(item && item.decision),
    submittedAt: String(item && item.submittedAt ? item.submittedAt : '').trim()
  }
}

function committeeMembersMatch(left, right) {
  const leftKeys = [
    left && left.reviewerId,
    left && left.reviewerName
  ].map(normalizeComparable).filter(Boolean)
  const rightKeys = [
    right && right.reviewerId,
    right && right.reviewerName
  ].map(normalizeComparable).filter(Boolean)

  return leftKeys.some(value => rightKeys.includes(value))
}

function mergeCommitteeMembers(existing, incoming) {
  return {
    reviewerId: existing.reviewerId || incoming.reviewerId || '',
    reviewerName: existing.reviewerName || incoming.reviewerName || '',
    decision: incoming.decision || existing.decision || '',
    submittedAt: incoming.submittedAt || existing.submittedAt || ''
  }
}

function pushUniqueCommitteeMember(items, incoming) {
  const normalizedIncoming = normalizeCommitteeMember(incoming)
  const index = items.findIndex(item => committeeMembersMatch(item, normalizedIncoming))
  if (index === -1) {
    items.push(normalizedIncoming)
    return items
  }
  items.splice(index, 1, mergeCommitteeMembers(items[index], normalizedIncoming))
  return items
}

function buildCommitteeCommentParticipants(comments = [], reviewers = [], fallbackLabel = 'Committee member') {
  return comments
    .filter(comment => normalizeComparable(comment && comment.role).includes('committee'))
    .reduce((items, comment) => {
      const authorName = String(comment && comment.author ? comment.author : '').trim()
      if (!authorName) return items
      const matchedReviewer = reviewers.find((reviewer) => committeeMembersMatch(reviewer, {
        reviewerId: '',
        reviewerName: authorName
      }))
      return pushUniqueCommitteeMember(items, matchedReviewer || {
        reviewerId: '',
        reviewerName: authorName,
        decision: '',
        submittedAt: ''
      })
    }, [])
    .map(item => normalizeCommitteeMember(item, fallbackLabel))
}

function buildCommitteeDisplayRoster({ comments = [], assigned = [], states = [], fallbackLabel = 'Committee member' } = {}) {
  const roster = []
  const normalizedStates = states.map(item => normalizeCommitteeMember(item, fallbackLabel))
  const normalizedAssigned = assigned.map(item => normalizeCommitteeMember(item, fallbackLabel))
  const commentParticipants = buildCommitteeCommentParticipants(comments, normalizedAssigned.concat(normalizedStates), fallbackLabel)

  normalizedAssigned.forEach(item => pushUniqueCommitteeMember(roster, item))
  commentParticipants.forEach(item => pushUniqueCommitteeMember(roster, item))
  normalizedStates.forEach(item => pushUniqueCommitteeMember(roster, item))

  if (!roster.length) {
    commentParticipants.forEach(item => pushUniqueCommitteeMember(roster, item))
    normalizedStates.forEach(item => pushUniqueCommitteeMember(roster, item))
  }

  return roster.map((member) => {
    const matchedState = normalizedStates.find(item => committeeMembersMatch(item, member))
    return mergeCommitteeMembers(member, matchedState || {})
  })
}

const PAGE_TEXT = {
  en: {
    reviewEyebrow: 'Admin Review',
    finalApprovalEyebrow: 'Admin Final Decision',
    reviewTitle: 'Review',
    finalApprovalTitle: 'Final Approval',
    reviewSubtitle: 'Monitor committee comments, see who has sent back the current form, and track which committee members are still reviewing.',
    finalApprovalSubtitle: 'Review the document, committee feedback, and user comments before approving or disapproving the agreement.',
    loadingDots: 'Loading...',
    loading: 'Loading',
    previewTitle: 'MOU Draft Preview and Detail',
    approvedPreviewTitle: 'Approved Document Preview',
    loadingDraft: 'Loading Word draft from the database...',
    wordAttached: 'Word document attached',
    openDocument: 'Open document',
    noWordDocument: 'No Word document available',
    noWordDocumentDetail: 'No attached DOC or DOCX was found, and the agreement draft content is also empty.',
    commentsTitle: 'Committee and User Comments',
    general: 'General',
    page: 'Page',
    noComments: 'No comments were recorded for this agreement.',
    detailsTitle: 'Agreement Details',
    committeeProgressTitle: 'Committee Review Progress',
    committeeApprovedCount: 'Done',
    committeePendingCount: 'Still in review',
    committeeApprovedMembers: 'Done',
    committeePendingMembers: 'Still reviewing',
    committeeApprovedEmpty: 'No committee members have submitted back yet.',
    committeePendingEmpty: 'All participating committee members are done.',
    committeePendingHidden: 'Committee members will appear here after they comment or submit back.',
    committeeMemberFallback: 'Committee member',
    fields: {
      title: 'Title',
      createdBy: 'Created by',
      partner: 'Partner',
      type: 'Type',
      department: 'Department',
      submittedOn: 'Submitted on',
      committeeMembers: 'Committee members',
      totalComments: 'Total comments',
      summary: 'Summary'
    },
    noSummary: 'No summary provided.',
    finalDecision: 'Final Decision',
    reviewStatus: 'Review Status',
    approve: 'Approve',
    approveHelp: 'Approve the agreement and close the workflow.',
    disapprove: 'Disapprove',
    disapproveHelp: 'Send the agreement back to the user with an admin note.',
    adminNote: 'Admin note',
    adminNotePlaceholder: 'Explain why the agreement is being returned.',
    submitFinalDecision: 'Submit Final Decision',
    openFinalApproval: 'Open Final Approval',
    reviewWaitingTitle: 'Committee Review Is Still In Progress',
    reviewCompletedTitle: 'Committee Review Is Ready For Final Approval',
    reviewCompletedHelp: 'All participating committee members have submitted back the current form. Continue to final approval when you are ready.',
    loadingWorkspace: 'Loading admin review workspace...',
    backToQueue: 'Back to Queue',
    approved: 'Approved',
    awaitingFinalApproval: 'Awaiting Final Approval',
    committeeReviewInProgress: 'Committee Review In Progress',
    awaitingCommitteeApprovalsTitle: 'Waiting for committee approvals',
    mouDraft: 'MOU Draft',
    noWordDraftAttached: 'No Word draft is attached for this MOU yet.',
    unableToLoadWordDraft: 'Unable to load the MOU Word document from the database.',
    finalApprovedAgreement: 'Final Approved Agreement.docx',
    agreementApprovedTitle: 'Agreement approved by admin',
    agreementApprovedUser: 'Your agreement',
    agreementApprovedAny: 'An agreement',
    approvedMessageUser: 'was approved by admin.',
    approvedMessageCommittee: 'was approved by admin.',
    sourceMyAgreements: 'My Agreements',
    sourceReviewHistory: 'Review History',
    viewApprovedAgreement: 'View approved agreement',
    openReviewHistory: 'Open review history',
    nextApprovedUser: 'Open the approved agreement and review the final status.',
    nextApprovedCommittee: 'Open review history and check the final approved result.',
    returnedTitle: 'Agreement returned by admin',
    returnedUserDefault: 'Your agreement',
    returnedAnyDefault: 'An agreement',
    returnedMessage: 'was returned by admin and needs further updates.',
    sourceMyReviewQueue: 'My Review Queue',
    openAgreement: 'Open agreement',
    openRevision: 'Open revision',
    nextReturned: 'Review the admin decision, revise the draft, and resubmit.',
    warningRejectRequired: 'Please provide the reason for disapproval before submitting.',
    warningRejectTitle: 'Reason Required',
    warningDecisionRequired: 'Please choose approve or disapprove first.',
    warningDecisionTitle: 'Decision Required'
  },
  th: {
    reviewEyebrow: 'ตรวจพิจารณาโดยผู้ดูแล',
    finalApprovalEyebrow: 'การตัดสินใจขั้นสุดท้ายของผู้ดูแล',
    reviewTitle: 'Review',
    finalApprovalTitle: 'อนุมัติขั้นสุดท้าย',
    reviewSubtitle: 'ติดตามความคิดเห็นของคณะกรรมการ ดูว่าใครส่งแบบฟอร์มกลับมาแล้ว และดูว่าใครยังอยู่ระหว่างพิจารณา',
    finalApprovalSubtitle: 'ตรวจสอบเอกสาร ความเห็นจากคณะกรรมการ และความคิดเห็นของผู้ใช้ก่อนอนุมัติหรือไม่อนุมัติข้อตกลง',
    loadingDots: 'กำลังโหลด...',
    loading: 'กำลังโหลด',
    previewTitle: 'ตัวอย่างร่าง MOU และรายละเอียด',
    approvedPreviewTitle: 'ตัวอย่างเอกสารที่อนุมัติแล้ว',
    loadingDraft: 'กำลังโหลดไฟล์ร่าง Word จากฐานข้อมูล...',
    wordAttached: 'มีไฟล์เอกสาร Word แนบอยู่',
    openDocument: 'เปิดเอกสาร',
    noWordDocument: 'ไม่มีเอกสาร Word',
    noWordDocumentDetail: 'ไม่พบไฟล์ DOC หรือ DOCX ที่แนบไว้ และเนื้อหาร่างข้อตกลงก็ว่างเปล่า',
    commentsTitle: 'ความคิดเห็นจากคณะกรรมการและผู้ใช้',
    general: 'ทั่วไป',
    page: 'หน้า',
    noComments: 'ไม่มีความคิดเห็นสำหรับข้อตกลงนี้',
    detailsTitle: 'รายละเอียดข้อตกลง',
    committeeProgressTitle: 'ความคืบหน้าการพิจารณาของคณะกรรมการ',
    committeeApprovedCount: 'เสร็จแล้ว',
    committeePendingCount: 'กำลังพิจารณา',
    committeeApprovedMembers: 'ดำเนินการเสร็จแล้ว',
    committeePendingMembers: 'ยังอยู่ระหว่างพิจารณา',
    committeeApprovedEmpty: 'ยังไม่มีกรรมการส่งแบบฟอร์มกลับ',
    committeePendingEmpty: 'กรรมการที่เข้าร่วมพิจารณาดำเนินการครบแล้ว',
    committeePendingHidden: 'รายชื่อกรรมการจะแสดงที่นี่เมื่อมีการแสดงความคิดเห็นหรือส่งแบบฟอร์มกลับแล้ว',
    committeeMemberFallback: 'กรรมการ',
    fields: {
      title: 'ชื่อเรื่อง',
      createdBy: 'สร้างโดย',
      partner: 'คู่สัญญา',
      type: 'ประเภท',
      department: 'หน่วยงาน',
      submittedOn: 'ส่งเมื่อ',
      committeeMembers: 'จำนวนกรรมการ',
      totalComments: 'ความคิดเห็นทั้งหมด',
      summary: 'สรุป'
    },
    noSummary: 'ไม่มีสรุป',
    finalDecision: 'การตัดสินใจขั้นสุดท้าย',
    reviewStatus: 'สถานะการพิจารณา',
    approve: 'อนุมัติ',
    approveHelp: 'อนุมัติข้อตกลงและปิดกระบวนการทำงาน',
    disapprove: 'ไม่อนุมัติ',
    disapproveHelp: 'ส่งข้อตกลงกลับให้ผู้ใช้พร้อมหมายเหตุจากผู้ดูแล',
    adminNote: 'หมายเหตุผู้ดูแล',
    adminNotePlaceholder: 'อธิบายเหตุผลที่ส่งข้อตกลงกลับ',
    submitFinalDecision: 'ส่งผลการตัดสินใจขั้นสุดท้าย',
    openFinalApproval: 'เปิดการอนุมัติขั้นสุดท้าย',
    reviewWaitingTitle: 'คณะกรรมการยังอยู่ระหว่างพิจารณา',
    reviewCompletedTitle: 'พร้อมเข้าสู่การอนุมัติขั้นสุดท้าย',
    reviewCompletedHelp: 'กรรมการที่เข้าร่วมทั้งหมดส่งแบบฟอร์มกลับครบแล้ว คุณสามารถไปที่การอนุมัติขั้นสุดท้ายได้เมื่อพร้อม',
    loadingWorkspace: 'กำลังโหลดหน้าต่างตรวจพิจารณาของผู้ดูแล...',
    backToQueue: 'กลับไปคิวงาน',
    approved: 'อนุมัติแล้ว',
    awaitingFinalApproval: 'รอการอนุมัติขั้นสุดท้าย',
    committeeReviewInProgress: 'อยู่ระหว่างการพิจารณาของคณะกรรมการ',
    awaitingCommitteeApprovalsTitle: 'กำลังรอการอนุมัติจากคณะกรรมการ',
    mouDraft: 'ร่าง MOU',
    noWordDraftAttached: 'ยังไม่มีไฟล์ร่าง Word สำหรับ MOU นี้',
    unableToLoadWordDraft: 'ไม่สามารถโหลดเอกสาร Word ของ MOU จากฐานข้อมูลได้',
    finalApprovedAgreement: 'ข้อตกลงที่อนุมัติแล้ว.docx',
    agreementApprovedTitle: 'ผู้ดูแลอนุมัติข้อตกลงแล้ว',
    agreementApprovedUser: 'ข้อตกลงของคุณ',
    agreementApprovedAny: 'มีข้อตกลงหนึ่งรายการ',
    approvedMessageUser: 'ได้รับการอนุมัติจากผู้ดูแลแล้ว',
    approvedMessageCommittee: 'ได้รับการอนุมัติจากผู้ดูแลแล้ว',
    sourceMyAgreements: 'ข้อตกลงของฉัน',
    sourceReviewHistory: 'ประวัติการพิจารณา',
    viewApprovedAgreement: 'ดูข้อตกลงที่อนุมัติแล้ว',
    openReviewHistory: 'เปิดประวัติการพิจารณา',
    nextApprovedUser: 'เปิดดูข้อตกลงที่อนุมัติแล้วและตรวจสอบสถานะสุดท้าย',
    nextApprovedCommittee: 'เปิดประวัติการพิจารณาและตรวจสอบผลลัพธ์ที่อนุมัติแล้ว',
    returnedTitle: 'ผู้ดูแลส่งข้อตกลงกลับแล้ว',
    returnedUserDefault: 'ข้อตกลงของคุณ',
    returnedAnyDefault: 'มีข้อตกลงหนึ่งรายการ',
    returnedMessage: 'ถูกส่งกลับโดยผู้ดูแลและต้องแก้ไขเพิ่มเติม',
    sourceMyReviewQueue: 'คิวตรวจทานของฉัน',
    openAgreement: 'เปิดข้อตกลง',
    openRevision: 'เปิดงานแก้ไข',
    nextReturned: 'ตรวจสอบคำตัดสินของผู้ดูแล แก้ไขร่าง และส่งใหม่',
    warningRejectRequired: 'กรุณาระบุเหตุผลที่ไม่อนุมัติก่อนส่ง',
    warningRejectTitle: 'ต้องระบุเหตุผล',
    warningDecisionRequired: 'กรุณาเลือกอนุมัติหรือไม่อนุมัติก่อน',
    warningDecisionTitle: 'ต้องเลือกผลการตัดสินใจ'
  }
}

export default {
  name: 'AdminFinalApprovalPage',
  components: {
    AgreementDocumentHeader,
    AgreementSignaturePreview
  },
  data() {
    return {
      agreement: null,
      previewAttachment: null,
      previewLoading: false,
      previewError: '',
      selectedDecision: '',
      decisionNote: ''
    }
  },
  computed: {
    localeCode() {
      return String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'en').toLowerCase().startsWith('th') ? 'th' : 'en'
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    reviewId() {
      return String(this.$route.params.id || this.$route.query.agreementId || '')
    },
    isFinalApprovalRoute() {
      return String(this.$route.name || '') === 'Final Approval'
    },
    workspaceEyebrow() {
      return this.isFinalApprovalRoute ? this.text.finalApprovalEyebrow : this.text.reviewEyebrow
    },
    workspaceTitle() {
      return this.isFinalApprovalRoute ? this.text.finalApprovalTitle : this.text.reviewTitle
    },
    workspaceSubtitle() {
      return this.isFinalApprovalRoute ? this.text.finalApprovalSubtitle : this.text.reviewSubtitle
    },
    activePreviewTitle() {
      return this.agreement && this.agreement.status === 'approved'
        ? this.text.approvedPreviewTitle
        : this.text.previewTitle
    },
    actionPanelTitle() {
      return this.isFinalApprovalRoute ? this.text.finalDecision : this.text.reviewStatus
    },
    showDecisionControls() {
      return this.isFinalApprovalRoute
        && !!this.agreement
        && this.agreement.status !== 'approved'
        && this.readyForAdminFinalApproval
    },
    statusLabel() {
      if (!this.agreement) return ''
      if (this.agreement.status === 'approved') return this.text.approved
      return this.readyForAdminFinalApproval ? this.text.awaitingFinalApproval : this.text.committeeReviewInProgress
    },
    committeeDecisionStates() {
      const states = this.agreement && Array.isArray(this.agreement.committeeMemberStates)
        ? this.agreement.committeeMemberStates
        : []
      return states.map(item => normalizeCommitteeMember(item, this.text.committeeMemberFallback))
    },
    visibleCommitteeDecisionStates() {
      return this.committeeDecisionStates.filter(item => item.decision || item.submittedAt)
    },
    displayComments() {
      const comments = this.agreement && Array.isArray(this.agreement.comments) ? [...this.agreement.comments] : []
      return comments.sort((left, right) => {
        const leftTime = new Date(left && (left.date || left.time) || 0).getTime()
        const rightTime = new Date(right && (right.date || right.time) || 0).getTime()
        return rightTime - leftTime
      })
    },
    committeeReviewStates() {
      return buildCommitteeDisplayRoster({
        comments: this.displayComments,
        assigned: [],
        states: this.visibleCommitteeDecisionStates,
        fallbackLabel: this.text.committeeMemberFallback
      })
    },
    approvedCommitteeMembers() {
      return this.committeeReviewStates.filter(item => item.decision || item.submittedAt)
    },
    pendingCommitteeMembers() {
      return this.committeeReviewStates.filter(item => !(item.decision || item.submittedAt))
    },
    trackedCommitteeCount() {
      return this.committeeReviewStates.length
    },
    committeeApprovedCount() {
      return this.approvedCommitteeMembers.length
    },
    committeePendingCount() {
      return this.pendingCommitteeMembers.length
    },
    committeeRequiredCount() {
      return this.trackedCommitteeCount
    },
    readyForAdminFinalApproval() {
      return isReadyForAdminFinalApproval(this.agreement)
    },
    pendingCommitteeEmptyMessage() {
      return this.trackedCommitteeCount > 0
        ? this.text.committeePendingEmpty
        : this.text.committeePendingHidden
    },
    committeeApprovalPendingMessage() {
      if (this.readyForAdminFinalApproval) return ''
      const pendingCount = this.committeePendingCount
      const pendingReviewers = this.pendingCommitteeMembers
        .map(item => item.reviewerName)
        .filter(Boolean)
      if (this.localeCode === 'th') {
        if (pendingReviewers.length) {
          return `หน้านี้ยังรอกรรมการส่งแบบฟอร์มกลับอีก ${pendingCount} คน: ${pendingReviewers.join(', ')}`
        }
        return 'กรรมการทุกคนยังอยู่ระหว่างพิจารณาอยู่ในรอบปัจจุบัน'
      }
      if (pendingReviewers.length) {
        return `This review is still waiting on ${pendingCount} committee member${pendingCount === 1 ? '' : 's'} to send back the current form: ${pendingReviewers.join(', ')}.`
      }
      return 'Committee members are still reviewing the current form.'
    },
    previewUrl() {
      if (!this.previewAttachment) return ''
      return this.previewAttachment.url || this.previewAttachment.fileUrl || ''
    },
    previewAttachmentLabel() {
      if (!this.previewAttachment) return this.agreement ? this.agreement.fileLabel : this.text.mouDraft
      return this.previewAttachment.originalName || this.previewAttachment.fileName || (this.agreement && this.agreement.fileLabel) || this.text.mouDraft
    },
    documentSections() {
      if (!this.agreement) return []
      const draftPreviewSections = normalizeAgreementSections(this.agreement.draftPreviewSections)
      if (draftPreviewSections.length) {
        return draftPreviewSections
          .map((item, index) => ({
            key: String(item && item.key ? item.key : `section-${index + 1}`),
            title: String(item && item.title ? item.title : `Section ${index + 1}`),
            content: String(item && item.content ? item.content : '')
          }))
          .filter((item) => item.content)
      }

      const articleDrafts = normalizeAgreementSections(this.agreement.articleDrafts)
      if (articleDrafts.length) {
        return articleDrafts
          .map((item, index) => ({
            key: String(item && item.key ? item.key : `article-${index + 1}`),
            title: String(item && item.title ? item.title : `Article ${index + 1}`),
            content: String(item && item.content ? item.content : '')
          }))
          .filter((item) => item.content)
      }

      const customDocument = this.agreement.customDocument
      if (customDocument && customDocument.content) {
        return [{
          key: 'custom-document',
          title: customDocument.title || 'Agreement Content',
          content: customDocument.content
        }]
      }

      if (this.agreement.summary) {
        return [{
          key: 'summary',
          title: 'Agreement Summary',
          content: this.agreement.summary
        }]
      }

      return []
    },
    draftPreviewSections() {
      return this.documentSections
    }
  },
  watch: {
    reviewId: {
      immediate: true,
      handler() {
        this.loadAgreement()
      }
    }
  },
  methods: {
    async loadAgreement() {
      if (!this.reviewId) {
        this.agreement = null
        return
      }
      const response = await api.adminReview('detail', { id: this.reviewId })
      this.agreement = response && response.data && response.data.data ? response.data.data : null
      this.selectedDecision = ''
      this.decisionNote = this.agreement ? this.agreement.decisionNote || '' : ''
      await this.loadPreviewAttachment()
    },
    async loadPreviewAttachment() {
      if (!this.agreement) return
      this.previewLoading = true
      this.previewError = ''
      this.previewAttachment = null
      try {
        const response = await api.attachments('list', {
          ownerType: this.agreement.attachmentOwnerType || 'mou-review',
          ownerId: this.agreement.attachmentOwnerId || `mou-${this.agreement.id}`
        })
        const rawList = Array.isArray(response && response.data && response.data.data)
          ? response.data.data
          : Array.isArray(response && response.data) ? response.data : []
        const documentAttachment = rawList.find(item => {
          const mime = String(item.mimeType || '').toLowerCase()
          const name = String(item.originalName || item.fileName || '').toLowerCase()
          return mime.includes('msword') || mime.includes('wordprocessingml') || name.endsWith('.doc') || name.endsWith('.docx')
        }) || null
        this.previewAttachment = documentAttachment || this.buildAgreementDraftAttachment()
        if (!this.previewAttachment) this.previewError = this.text.noWordDraftAttached
      } catch (error) {
        this.previewAttachment = this.buildAgreementDraftAttachment()
        if (!this.previewAttachment && !this.draftPreviewSections.length) {
          this.previewError = this.text.unableToLoadWordDraft
        }
      } finally {
        this.previewLoading = false
      }
    },
    buildAgreementDraftAttachment() {
      const finalDocument = this.agreement && this.agreement.finalDocument ? this.agreement.finalDocument : null
      if (finalDocument && finalDocument.url) {
        return {
          originalName: finalDocument.fileName || this.text.finalApprovedAgreement,
          fileName: finalDocument.fileName || this.text.finalApprovedAgreement,
          mimeType: finalDocument.mimeType || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          url: finalDocument.url,
          fileUrl: finalDocument.url
        }
      }
      const uploaded = this.agreement && this.agreement.uploadedDraft ? this.agreement.uploadedDraft : null
      if (uploaded && uploaded.url) {
        return {
          originalName: uploaded.name || this.agreement.fileLabel,
          fileName: uploaded.name || this.agreement.fileLabel,
          mimeType: uploaded.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          url: uploaded.url,
          fileUrl: uploaded.url
        }
      }
      return null
    },
    goToFinalApproval() {
      if (!this.reviewId) return
      this.$router.push(`/review-queue/${this.reviewId}/final-approval`)
    },
    chooseDecision(type) {
      this.selectedDecision = type
      if (type !== 'reject') this.decisionNote = ''
    },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    sectionDisplayTitle(section) {
      return getAgreementSectionDisplayTitle(section)
    },
    isCenteredDocumentSection(section) {
      return isCenteredAgreementSection(section, this.agreement && this.agreement.type)
    },
    isSignatureDocumentSection(section) {
      return isSignatureAgreementSection(section)
    },
    async approveAgreement() {
      const response = await api.adminReview('final-decision', {
        id: this.agreement.id,
        decision: 'approve',
        decisionNote: this.decisionNote
      })
      const nextItem = response && response.data && response.data.data ? response.data.data : null
      this.agreement = nextItem
      if (nextItem) {
        createWorkflowNotification({
          role: 'user',
          audienceKey: nextItem.ownerEmail || '',
          type: 'approval',
          title: this.text.agreementApprovedTitle,
          message: `${nextItem.title || this.text.agreementApprovedUser} ${this.text.approvedMessageUser}`,
          source: this.text.sourceMyAgreements,
          partnerName: nextItem.partner || '',
          reference: `MOU-${nextItem.id}`,
          priority: 'high',
          targetPath: `/dashboard/user-agreements/${nextItem.id}/review-summary?mode=view`,
          actionLabel: this.text.viewApprovedAgreement,
          nextStep: this.text.nextApprovedUser
        })
        createWorkflowNotification({
          role: 'committee',
          type: 'approval',
          title: this.text.agreementApprovedTitle,
          message: `${nextItem.title || this.text.agreementApprovedAny} ${this.text.approvedMessageCommittee}`,
          source: this.text.sourceReviewHistory,
          partnerName: nextItem.partner || '',
          reference: `MOU-${nextItem.id}`,
          priority: 'medium',
          targetPath: '/review-history',
          actionLabel: this.text.openReviewHistory,
          nextStep: this.text.nextApprovedCommittee
        })
      }
      this.$router.push('/agreements')
    },
    async rejectAgreement() {
      const response = await api.adminReview('final-decision', {
        id: this.agreement.id,
        decision: 'reject',
        decisionNote: this.decisionNote
      })
      const nextItem = response && response.data && response.data.data ? response.data.data : null
      this.agreement = nextItem
      if (nextItem) {
        createWorkflowNotification({
          role: 'user',
          audienceKey: nextItem.ownerEmail || '',
          type: 'review',
          title: this.text.returnedTitle,
          message: `${nextItem.title || this.text.returnedUserDefault} ${this.text.returnedMessage}`,
          source: this.text.sourceMyReviewQueue,
          partnerName: nextItem.partner || '',
          reference: `MOU-${nextItem.id}`,
          priority: 'high',
          targetPath: `/dashboard/user-agreements/${nextItem.id}/start-review`,
          actionLabel: this.text.openRevision,
          nextStep: this.text.nextReturned
        })
        createWorkflowNotification({
          role: 'committee',
          dedupeKey: `committee-admin-reject-${nextItem.id}-${Date.now()}`,
          type: 'review',
          title: this.text.returnedTitle,
          message: `${nextItem.title || this.text.returnedAnyDefault} ${this.text.returnedMessage}`,
          source: this.text.sourceReviewHistory,
          partnerName: nextItem.partner || '',
          reference: `MOU-${nextItem.id}`,
          priority: 'medium',
          targetPath: '/review-history',
          actionLabel: this.text.openReviewHistory,
          nextStep: this.text.nextReturned
        })
      }
      this.$router.push('/review-queue')
    },
    submitDecision() {
      if (!this.readyForAdminFinalApproval) {
        showWarningAlert(this.committeeApprovalPendingMessage, this.text.awaitingCommitteeApprovalsTitle)
        return
      }
      if (this.selectedDecision === 'approve') {
        this.approveAgreement()
        return
      }
      if (this.selectedDecision === 'reject') {
        if (!this.decisionNote.trim()) {
          showWarningAlert(this.text.warningRejectRequired, this.text.warningRejectTitle)
          return
        }
        this.rejectAgreement()
        return
      }
      showWarningAlert(this.text.warningDecisionRequired, this.text.warningDecisionTitle)
    }
  }
}
</script>

<style scoped>
.admin-action-page {
  min-height: 100vh;
  padding: 24px;
}

.action-shell {
  border-radius: 22px;
}

.page-head {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 20px;
}

.eyebrow {
  color: #9e212f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.page-head h1 {
  color: #2d3748;
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 6px;
}

.page-head p,
.empty-state,
.comment-copy,
.draft-preview-card__copy {
  color: #718096;
}

.page-head__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.meta-chip {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #475569;
  font-weight: 700;
  padding: 10px 14px;
}

.meta-chip--status {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
}

.action-layout {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.9fr);
}

.action-main,
.action-side,
.comment-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.surface-card {
  background: #fafafa;
  border: 1px solid #edf2f7;
  border-radius: 18px;
  padding: 18px;
}

.surface-title {
  color: #2d3748;
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 14px;
}

.pdf-sheet {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
}

.pdf-topline {
  color: #718096;
  display: flex;
  font-size: 0.9rem;
  font-weight: 600;
  justify-content: space-between;
  margin-bottom: 14px;
}

.pdf-sheet__branding {
  border-bottom: 1px solid #eef2f7;
  margin: 0 0 18px;
  padding-bottom: 18px;
}

.pdf-frame-wrap,
.pdf-frame {
  min-height: 620px;
}

.pdf-frame {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  width: 100%;
}

.draft-preview-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.draft-preview-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
}

.draft-preview-card__title {
  color: #2d3748;
  font-weight: 700;
  margin-bottom: 8px;
}

.draft-preview-card--centered .draft-preview-card__copy {
  text-align: center;
}

.detail-grid {
  display: grid;
  gap: 12px;
}

.detail-grid.compact .detail-row {
  padding: 10px 0;
}

.detail-row {
  align-items: flex-start;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 0;
}

.detail-row span,
.comment-meta {
  color: #718096;
}

.detail-row strong,
.comment-head strong {
  color: #2d3748;
}

.detail-row strong {
  max-width: 62%;
  text-align: right;
}

.detail-row--wide {
  align-items: flex-start;
  flex-direction: column;
}

.detail-row--wide strong {
  max-width: 100%;
  text-align: left;
}

.comment-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
}

.surface-card--comments .comment-list {
  max-height: 540px;
  overflow-y: auto;
  padding-right: 4px;
}

.committee-progress-card {
  gap: 14px;
}

.committee-progress-metrics {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 16px;
}

.committee-progress-metric {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
}

.committee-progress-metric span {
  color: #718096;
  display: block;
  font-size: 0.85rem;
  margin-bottom: 6px;
}

.committee-progress-metric strong {
  color: #2d3748;
  font-size: 1.2rem;
  font-weight: 700;
}

.committee-progress-metric--approved {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.committee-progress-metric--pending {
  background: #fff7ed;
  border-color: #fed7aa;
}

.committee-progress-group + .committee-progress-group {
  margin-top: 14px;
}

.committee-progress-label {
  color: #2d3748;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.committee-pill-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.committee-pill {
  border-radius: 999px;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 8px 12px;
}

.committee-pill--approved {
  background: #dcfce7;
  color: #166534;
}

.committee-pill--pending {
  background: #ffedd5;
  color: #9a3412;
}

.committee-progress-empty {
  color: #718096;
  font-size: 0.9rem;
}

.comment-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

.comment-meta {
  font-size: 0.88rem;
  margin-bottom: 8px;
}

.decision-choice-grid {
  display: grid;
  gap: 12px;
}

.decision-choice {
  align-items: flex-start;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 112px;
  padding: 16px;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, background 0.18s ease;
}

.decision-choice strong {
  font-size: 1.02rem;
}

.decision-choice span {
  line-height: 1.55;
}

.decision-choice:hover {
  transform: translateY(-1px);
}

.decision-choice--approve {
  background: linear-gradient(180deg, #f7fff9 0%, #eefbf2 100%);
  border-color: #b7e4c7;
}

.decision-choice--approve strong {
  color: #166534;
}

.decision-choice--approve span {
  color: #2f855a;
}

.decision-choice--approve:hover {
  border-color: #38a169;
  box-shadow: 0 14px 28px rgba(22, 163, 74, 0.12);
}

.decision-choice--reject {
  background: linear-gradient(180deg, #fff8f7 0%, #fff0ee 100%);
  border-color: #fecaca;
}

.decision-choice--reject strong {
  color: #b42318;
}

.decision-choice--reject span {
  color: #c2410c;
}

.decision-choice--reject:hover {
  border-color: #f87171;
  box-shadow: 0 14px 28px rgba(220, 38, 38, 0.12);
}

.decision-choice--selected {
  transform: translateY(-1px);
}

.decision-choice--approve.decision-choice--selected {
  background: linear-gradient(180deg, #effdf4 0%, #dcfce7 100%);
  border-color: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.14), 0 16px 30px rgba(22, 163, 74, 0.16);
}

.decision-choice--reject.decision-choice--selected {
  background: linear-gradient(180deg, #fff1f2 0%, #ffe2e6 100%);
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14), 0 16px 30px rgba(220, 38, 38, 0.16);
}

.decision-label {
  color: #2d3748;
  display: block;
  font-weight: 700;
  margin: 16px 0 8px;
}

.decision-note {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 14px;
  resize: vertical;
  width: 100%;
}

.decision-gate-note {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 14px;
  color: #9a3412;
  font-weight: 600;
  margin-bottom: 14px;
  padding: 12px 14px;
}

.review-mode-panel {
  background: linear-gradient(180deg, #fffdf8 0%, #fff8ef 100%);
  border: 1px solid #fde7c2;
  border-radius: 16px;
  padding: 16px;
}

.review-mode-panel__title {
  color: #2d3748;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.review-mode-panel__copy {
  color: #718096;
  margin: 0;
}

.decision-submit {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.empty-state {
  background: #fff;
  border: 1px dashed #d9e2ec;
  border-radius: 14px;
  padding: 18px;
  text-align: center;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 22px;
}

@media (max-width: 991px) {
  .action-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .admin-action-page {
    padding: 16px;
  }

  .page-head,
  .footer-actions,
  .pdf-topline,
  .detail-row,
  .comment-head {
    flex-direction: column;
  }

  .footer-actions {
    gap: 12px;
  }

  .committee-progress-metrics {
    grid-template-columns: 1fr;
  }

  .detail-row strong {
    max-width: 100%;
    text-align: left;
  }
}
</style>
